import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_DASHBOARD_ORGANIZATION_SUMMARY_PATH,
  GetDashboardOrganizationSummaryErrorDto,
  GetDashboardOrganizationSummarySuccessDto,
} from '@/api/rest/resources/types/organization';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useNCAParentOrganizationId } from '@/pages/shared/hooks/useNCAParentOrganizationId';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type SuccessDto = GetDashboardOrganizationSummarySuccessDto;
type ErrorDto = GetDashboardOrganizationSummaryErrorDto;

export const useDashboardOrganizationSummary = (organizationIdArg?: string, plotGroupIdArg?: string | null) => {
  const orgId = useOrganizationId();
  const plotGroupId = usePlotGroupId();
  const ncaParentId = useNCAParentOrganizationId();

  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const pathParams = { id: organizationId };

  const queryParams = {
    plot_group_id: plotGroupIdArg ?? plotGroupId,
    ...(ncaParentId ? { nca_parent_id: ncaParentId } : {}),
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_DASHBOARD_ORGANIZATION_SUMMARY_PATH, pathParams, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', 'organization-summary', pathParams, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
